import { isOpenPartnerIncentiveModal } from "@/atoms/user/is-open-partner-incentive-modal";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { Body14RegularInput } from "@/components/input/Body14RegularInput";
import { Body14Regular } from "@/components/typography/Body14Regular";
import { userService } from "@/services/user-service";
import { ItemDetailDto } from "@/types/dto/item-detail-dto";
import { UserDto } from "@/types/dto/user-dto";
import { Checkbox, Table } from "flowbite-react";
import { useRecoilState } from "recoil";
import { ButtonColor } from "../../../common/enum/button-color";
import { CheckboxColor } from "../../../common/enum/checkbox-color";
import { formatCommasNumber } from "../../../common/util/format-number-with-commas";
import { StatusButton } from "../../button/StatusButton";
import { CustomCell } from "../CustomCell";
import moment from "moment-timezone";
import { DiscountDto } from "@/types/dto/discount-dto";

interface Props {
  user: UserDto;
  searchItemName: string;
  data: ItemDetailDto;
  checkedItemIds: number[];
  gradeDiscount: number;
  addCheckedItemId: (id: number) => void;
  removeCheckedItemId: (id: number) => void;
  getPage: (searchItemName: string) => void;
  openIncentivesModal: () => void;
  discount: DiscountDto | undefined;
}

export const PartnerItemTableRow: React.FC<Props> = ({
  user,
  searchItemName,
  data,
  checkedItemIds,
  gradeDiscount,
  addCheckedItemId,
  removeCheckedItemId,
  getPage,
  openIncentivesModal,
  discount,
}) => {
  const [isPartnerIncentiveModalOpen, setIsPartnerIncentiveModalOpen] =
    useRecoilState(isOpenPartnerIncentiveModal);
  const item = data;

  const isChecked = checkedItemIds.includes(item.id);

  const isIncentiveExpired = discount?.endDate
    ? moment(discount?.endDate).isBefore(moment())
    : false;

  const handleCheck = () => {
    if (isChecked) {
      removeCheckedItemId(item.id);
    } else {
      addCheckedItemId(item.id);
    }
  };

  const handleSubmit = async (rate: number) => {
    const now = new Date();
    try {
      now.setHours(0, 0, 0, 0);
      const response = await userService.incentiveDiscounts(user.id, {
        fromDate: now.toISOString(),
        discountRate: rate,
        itemIds: [item.id],
        delete: false,
      });
      if (!response.ok) {
        alert(GLOBAL_ERROR);
        return;
      }
      getPage(searchItemName);
    } catch (error) {
      console.error(error);
    }
  };

  const handleQuantityChange = (value: string) => {
    const numberValue = Number(value);
    if (numberValue > 9 && numberValue < 100) {
      handleSubmit(numberValue);
    }
  };

  const handleIncenctiveOffButtonClick = () => {
    setIsPartnerIncentiveModalOpen({
      user,
      itemId: item.id,
      getPage,
      searchItemName,
    });
  };

  return (
    <Table.Row key={item.id} className="bg-white text-semantic-text-body">
      <Table.Cell>
        <CustomCell>
          <Body14Regular className="text-semantic-text-body">
            <Checkbox
              color={isChecked ? CheckboxColor.DARK : ""}
              checked={isChecked}
              onChange={handleCheck}
            />
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          {item.imageUrls.length > 0 && (
            <img
              src={item.imageUrls[0]}
              alt={item.name}
              className="w-[32px] mr-[8px]"
            />
          )}
          <Body14Regular>{item.name}</Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular>{item.category.name}</Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell className="flex items-center">
        <CustomCell>
          <Body14Regular className="mx-[10px]">
            {item.minOrderQuantity}건
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell>
        <CustomCell>
          <Body14Regular>
            {item.discountRate > gradeDiscount
              ? `상품 할인 ${item.discountRate}%`
              : `등급 할인 ${gradeDiscount}%`}
          </Body14Regular>
        </CustomCell>
      </Table.Cell>
      <Table.Cell className="flex items-center">
        <div className="flex items-center justify-center gap-[8px]">
          <div
            className={`flex ${discount && !isIncentiveExpired
              ? "text-secondary-2-red"
              : "text-semantic-text-secondary"
              }`}
          >
            <Body14RegularInput
              key={item.incentiveDiscount?.discountRate}
              minNumber={0}
              maxNumber={100}
              onChange={(value) => handleQuantityChange(value)}
              disabled={true}
            >
              {discount && !isIncentiveExpired ? discount.discountRate : "0"}
            </Body14RegularInput>
            <Body14Regular>%</Body14Regular>
          </div>

          {(discount?.startDate && discount?.endDate) ? (
            <div className="flex gap-4">
              <div className="flex items-center gap-4">
                <StatusButton
                  color={ButtonColor.WARNING}
                  textColor="text-semantic-warning-warning-text"
                >
                  기간 ON
                </StatusButton>
                <Body14RegularInput
                  disabled={true}
                  minNumber={0}
                  onChange={() => { }}
                  className="flex-1"
                >
                  {!discount.endDate
                    ? "기간 없음"
                    : `${moment(discount.startDate).format(
                      "YYYY년 MM월 DD일"
                    )} ~ ${moment(discount.endDate).format(
                      "YYYY년 MM월 DD일"
                    )}`}
                </Body14RegularInput>
              </div>
            </div>
          ) : (
            <div className="cursor-pointer">
              <StatusButton color={ButtonColor.CUSTOM_GRAY}>
                기간 OFF
              </StatusButton>
            </div>
          )}
        </div>
      </Table.Cell>
      <Table.Cell>
        <CustomCell className="gap-[4px]">
          <Body14Regular className="text-secondary-2-red">{`${item.totalDiscountRate}%`}</Body14Regular>
          {`${formatCommasNumber(item.totalPrice)}원`}
        </CustomCell>
      </Table.Cell>
    </Table.Row>
  );
};
